import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";

/**
 * PropertyGalleryLightbox — fullscreen photo viewer with keyboard, click,
 * and swipe navigation. Opens when the user clicks any photo in the bento
 * grid on the property detail page. Renders a portal-like fixed overlay,
 * NOT a shadcn Dialog, so we get full-viewport black backdrop + edge-to-edge
 * images with zero max-width constraints.
 *
 * Controls
 * --------
 *   • Click a thumbnail on the gallery grid → opens with that index
 *   • ← / → arrow keys or on-screen arrows → navigate photos
 *   • Esc or the X button → close
 *   • Swipe left/right on touch devices → navigate
 *   • Tap outside the image (backdrop) → close
 *
 * Props
 * -----
 *   images: string[] — full URLs (Unsplash) or storage paths already resolved by IMG()
 *   startIndex: number
 *   onClose: () => void
 */
export default function PropertyGalleryLightbox({ images, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  // Zoom scale — when > 1 we suppress swipe-to-navigate (users are panning
  // around inside the zoomed photo, not switching photos). Bumped by the
  // TransformWrapper's onTransformed callback so it stays in sync.
  const [zoomScale, setZoomScale] = useState(1);
  const total = images.length;

  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);

  // Keyboard navigation — arrow keys + escape. Arrow keys are disabled
  // while zoomed so the user can't accidentally jump photos mid-pan.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && zoomScale <= 1.01) prev();
      else if (e.key === "ArrowRight" && zoomScale <= 1.01) next();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while lightbox is open — otherwise pinch-zoom on
    // mobile jitters between the underlying page and the lightbox.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [prev, next, onClose, zoomScale]);

  // Touch swipe — 40px threshold to distinguish a swipe from a tap.
  // Skipped when zoomed in (>1x) so panning doesn't trigger photo change.
  const [touchStartX, setTouchStartX] = useState(null);
  const onTouchStart = (e) => {
    if (zoomScale > 1.01) return;   // let the zoom-pan library handle pan
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchEnd = (e) => {
    if (touchStartX == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 40) {
      dx > 0 ? prev() : next();
    }
    setTouchStartX(null);
  };

  if (!images.length) return null;

  return (
    <div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Close */}
      <button
        data-testid="lightbox-close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center transition-colors z-10"
        aria-label="Close gallery"
      >
        <X size={20} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold tracking-widest uppercase z-10">
        {idx + 1} / {total}
      </div>

      {/* Prev */}
      {total > 1 && (
        <button
          data-testid="lightbox-prev"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="absolute left-2 md:left-6 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center transition-colors z-10"
          aria-label="Previous photo"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Image with pinch/wheel/double-tap zoom.
          keyed on `idx` so switching photos resets scale to 1 (fresh state). */}
      <div
        className="relative w-[92vw] h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <TransformWrapper
          key={idx}
          initialScale={1}
          minScale={1}
          maxScale={5}
          doubleClick={{ mode: "toggle", step: 2 }}
          pinch={{ step: 5 }}
          wheel={{ step: 0.15 }}
          panning={{ disabled: false, velocityDisabled: false }}
          onTransformed={(_ref, state) => setZoomScale(state.scale)}
          limitToBounds={true}
        >
          <>
            <TransformComponent
              wrapperClass="!w-full !h-full"
              contentClass="!w-full !h-full flex items-center justify-center"
            >
              <img
                data-testid="lightbox-image"
                src={images[idx]}
                alt={`Photo ${idx + 1} of ${total}`}
                className="max-w-[92vw] max-h-[85vh] object-contain rounded-lg shadow-2xl select-none"
                draggable={false}
              />
            </TransformComponent>
            <ZoomControls />
          </>
        </TransformWrapper>
      </div>

      {/* Next */}
      {total > 1 && (
        <button
          data-testid="lightbox-next"
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="absolute right-2 md:right-6 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center transition-colors z-10"
          aria-label="Next photo"
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Thumbnails strip — only on ≥md */}
      {total > 1 && (
        <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 gap-2 max-w-[90vw] overflow-x-auto no-scrollbar px-3 py-2 rounded-xl bg-white/5 backdrop-blur">
          {images.map((src, i) => (
            <button
              key={src + i}
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              className={`flex-none w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                i === idx ? "border-[#C9A24A] scale-105" : "border-transparent opacity-60 hover:opacity-100"
              }`}
              aria-label={`Go to photo ${i + 1}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * ZoomControls — small floating pill with zoom-in / zoom-out / reset buttons.
 * Uses the useControls() hook from react-zoom-pan-pinch so it must live
 * inside a TransformWrapper. Visible only when there's actual room to zoom
 * (i.e., always — but muted when scale === 1).
 */
function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  const btnCls =
    "w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition-colors";
  return (
    <div
      className="absolute bottom-3 right-3 md:bottom-6 md:right-6 flex items-center gap-2 p-1 rounded-full bg-black/50 backdrop-blur border border-white/10"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        data-testid="lightbox-zoom-out"
        onClick={() => zoomOut()}
        className={btnCls}
        aria-label="Zoom out"
      >
        <ZoomOut size={16} />
      </button>
      <button
        type="button"
        data-testid="lightbox-zoom-reset"
        onClick={() => resetTransform()}
        className={btnCls}
        aria-label="Reset zoom"
      >
        <Maximize2 size={14} />
      </button>
      <button
        type="button"
        data-testid="lightbox-zoom-in"
        onClick={() => zoomIn()}
        className={btnCls}
        aria-label="Zoom in"
      >
        <ZoomIn size={16} />
      </button>
    </div>
  );
}
